/**
 * Student reflection moderation verdict — Firestore schema and paths.
 *
 * One document per student reflection, living as a sibling leaf collection
 * alongside other section-analysis leaf collections:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/studentReflectionModeration/{userId}`
 *
 * The document id mirrors the reflection's document id (same `userId`),
 * making lookup a direct `getDoc` rather than a query.
 *
 * Verdicts are written server-side only (client writes are denied by Firestore
 * rules) to prevent students from spoofing their own moderation status.
 */

import {z} from 'zod';
import {FirestoreTimestampSchema} from '../../types';
import {
  parseSectionId,
  parseSectionWeekId,
  type SectionId,
  type SectionWeekId,
} from '../../../section/sectionApi';
import type {CourseId, UserId} from '../../../types/branded';
import {
  SECTION_ANALYSIS_REFLECTION_MODERATION_SUBCOLLECTION,
  getSectionAnalysisWeekChildCollectionPath,
} from './sectionAnalysisFirestorePaths';

// ============================================================================
// Status
// ============================================================================

/**
 * Categorical decision used by the SL hook to render or hide a reflection.
 *
 * - `'ok'`: model returned not-flagged at current policy; render the
 *   reflection.
 * - `'flagged'`: model flagged at least one tracked category; hide the
 *   reflection and contribute to the hidden-count.
 * - `'errored'`: moderation infrastructure failed terminally; hide silently
 *   without surfacing the reflection in any user-visible count.
 */
export const ReflectionModerationStatusSchema = z.enum([
  'ok',
  'flagged',
  'errored',
]);

/** Possible moderation outcomes for a single student reflection. */
export type ReflectionModerationStatus = z.infer<
  typeof ReflectionModerationStatusSchema
>;

// ============================================================================
// Category Scores
// ============================================================================

/**
 * Per-category confidence scores returned by the OpenAI moderation API.
 *
 * Keys are category identifiers (e.g. `'harassment'`, `'hate'`, `'violence'`).
 * Values are confidence scores in the closed interval [0, 1].
 *
 * Stored verbatim so future policy re-evaluations against updated thresholds
 * do not require re-calling the API.
 */
export const ReflectionModerationCategoryScoresSchema = z.record(
  z.string(), // category id (e.g. 'harassment', 'hate', 'violence')
  z.number(), // [0, 1] confidence (closed interval per OpenAI docs)
);

/** Map of OpenAI moderation category identifiers to their confidence scores. */
export type ReflectionModerationCategoryScores = z.infer<
  typeof ReflectionModerationCategoryScoresSchema
>;

// ============================================================================
// Verdict Document
// ============================================================================

/**
 * Schema for a student reflection moderation verdict Firestore document.
 *
 * Uses `.loose()` to allow additional Firestore metadata fields.
 * For compile-time branded type safety, use the
 * {@link StudentReflectionModerationVerdict} type.
 */
export const StudentReflectionModerationVerdictSchema = z
  .object({
    /**
     * Status used by the SL UI:
     *  - `'ok'`: render the reflection.
     *  - `'flagged'`: hide; contributes to the hidden-count.
     *  - `'errored'`: hide silently; not surfaced in any user-visible count.
     */
    status: ReflectionModerationStatusSchema,
    /**
     * Stable identifier for the model and threshold policy that produced this
     * verdict, e.g. `'omni-moderation-latest@2025-09-01+v1'`. The SL hook
     * compares this against the current policy's `modelVersion` to decide
     * whether an existing verdict is current or needs a refresh.
     */
    modelVersion: z.string(),
    /**
     * Server-set timestamp of when the verdict was written.
     *
     * Stored as a Firestore `serverTimestamp()` sentinel on write; reads back
     * as a Firestore {@link FirebaseTimestamp}. Use {@link FirestoreTimestampSchema}
     * to validate this field on read.
     */
    moderatedAt: FirestoreTimestampSchema,
    /**
     * Categories whose score crossed the policy threshold. Empty array when
     * `status === 'ok'`.
     */
    flaggedCategories: z.array(z.string()),
    /**
     * Full category score map. Stored even on `'ok'` verdicts so policy tuning
     * does not require re-calling the moderation API.
     */
    categoryScores: ReflectionModerationCategoryScoresSchema,
    /**
     * Source of the verdict, used for audit and re-moderation tracking.
     *  - `'callable'`: written by the on-demand SL-page callable.
     *  - `'backfill'`: written by the operator re-moderation script (PR 10).
     */
    source: z.enum(['callable', 'backfill']),
    /**
     * Optional error message when `status === 'errored'`. Truncated to 500
     * characters maximum to avoid potential PII leakage from upstream error
     * messages.
     */
    errorMessage: z.string().max(500).optional(),
    /**
     * Denormalized course identifier. Optional for back-compat with verdicts
     * written before this field was tracked; all new writes include it.
     * Enables `collectionGroup` queries filtered by course.
     */
    courseId: z.string().optional(),
    /**
     * Denormalized section identifier. Optional for back-compat with verdicts
     * written before this field was tracked.
     */
    sectionId: z.string().optional(),
    /**
     * Denormalized week number. Optional for back-compat with verdicts
     * written before this field was tracked.
     */
    sectionWeek: z.number().optional(),
  })
  .loose();

/**
 * Per-reflection moderation verdict stored in Firestore.
 *
 * For runtime validation, use {@link StudentReflectionModerationVerdictSchema}.
 * The `moderatedAt` field is a Firestore Timestamp on read; pass
 * `serverTimestamp()` when writing.
 */
export type StudentReflectionModerationVerdict = z.infer<
  typeof StudentReflectionModerationVerdictSchema
>;

// ============================================================================
// Paths
// ============================================================================

/**
 * Constructs the path to the student reflection moderation collection for a
 * given section week.
 *
 * Collection path:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/studentReflectionModeration`
 *
 * @param courseId - The course the section belongs to.
 * @param sectionId - The section whose reflections are being moderated.
 * @param sectionWeek - The section week whose reflections are being moderated.
 * @returns The Firestore collection path string.
 */
export function getStudentReflectionModerationCollectionPath({
  courseId,
  sectionId,
  sectionWeek,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
}): string {
  return getSectionAnalysisWeekChildCollectionPath({
    courseId,
    sectionId,
    sectionWeek,
    leafSubcollection: SECTION_ANALYSIS_REFLECTION_MODERATION_SUBCOLLECTION,
  });
}

/**
 * Constructs the path to a specific student reflection moderation verdict
 * document.
 *
 * Document path:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/studentReflectionModeration/{userId}`
 *
 * The document id matches the corresponding reflection document id (same
 * `userId`), so a verdict lookup requires only a direct `getDoc` call.
 *
 * @param courseId - The course the section belongs to.
 * @param sectionId - The section whose reflection is being moderated.
 * @param sectionWeek - The section week whose reflection is being moderated.
 * @param userId - The student's user id (mirrors the reflection document id).
 * @returns The Firestore document path string.
 */
export function getStudentReflectionModerationDocumentPath({
  courseId,
  sectionId,
  sectionWeek,
  userId,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  userId: UserId;
}): string {
  return `${getStudentReflectionModerationCollectionPath({courseId, sectionId, sectionWeek})}/${userId}`;
}

// ============================================================================
// Callable Function Definitions
// ============================================================================

/**
 * The exported Cloud Function name for the on-demand moderation callable.
 *
 * Must match the function export key (lowercase, no hyphens) so Firebase
 * deploys and the client SDK can resolve it by name.
 */
export const MODERATE_REFLECTIONS_FOR_WEEK_FUNCTION_NAME =
  'moderatereflectionsforweek' as const;

/**
 * Zod schema for the {@link ModerateReflectionsForWeekRequest} callable payload.
 *
 * Uses plain `z.string()` / `z.number()` for wire-safe parsing; the
 * {@link ModerateReflectionsForWeekRequest} interface carries branded types for
 * compile-time safety. Use {@link parseSectionId} / {@link parseSectionWeekId}
 * inside the callable to coerce and validate after schema parsing.
 */
export const ModerateReflectionsForWeekRequestSchema = z.object({
  /** The course ID the section belongs to. */
  courseId: z.string().min(1),
  /** The section ID whose reflections should be moderated. */
  sectionId: z.string().refine(s => parseSectionId(s) !== undefined, {
    message: 'Invalid sectionId — must be a non-empty string without slashes',
  }),
  /** The section week whose reflections should be moderated (integer >= 1). */
  sectionWeek: z.number().refine(n => parseSectionWeekId(n) !== undefined, {
    message: 'Invalid sectionWeek — must be an integer >= 1',
  }),
});

/**
 * Request payload for the {@link MODERATE_REFLECTIONS_FOR_WEEK_FUNCTION_NAME}
 * callable.
 *
 * Uses branded types for compile-time safety. For runtime validation, use
 * {@link ModerateReflectionsForWeekRequestSchema}.
 */
export interface ModerateReflectionsForWeekRequest {
  /** The course ID the section belongs to. */
  courseId: CourseId;
  /** The section ID whose reflections should be moderated. */
  sectionId: SectionId;
  /** The section week whose reflections should be moderated. */
  sectionWeek: SectionWeekId;
}

/**
 * Response returned by the {@link MODERATE_REFLECTIONS_FOR_WEEK_FUNCTION_NAME}
 * callable.
 */
export interface ModerateReflectionsForWeekResponse {
  /**
   * Number of verdicts written this invocation. Verdicts that were already
   * current (fresh `modelVersion`, `moderatedAt` newer than reflection's
   * `updatedAt`) are excluded — only newly written or overwritten verdicts
   * count.
   */
  moderatedCount: number;
  /**
   * Total number of student reflections found for the week (regardless of
   * whether they needed moderation).
   */
  totalCount: number;
}
